

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const faqs = [
  {
    q: "Why use Reputely instead of a pay-per-removal service?",
    a: "Pay-per-removal services focus on one outcome after a review has already become a problem. Reputely provides continuous reputation monitoring: we watch your catalog, detect policy issues, generate appeal-ready submissions, alert your team, and keep the workflow moving across every monitored product. Managed filing is currently available for brands with verified Brand Registry access.",
  },
  {
    q: "Can you guarantee a review will be removed?",
    a: "No, and don't trust any service that says yes. Amazon's team makes the final call. We find reviews that break Amazon rules, prepare a policy rationale, and keep you informed. For brands with verified Brand Registry access, we can also file eligible reports through Brand Registry Support.",
  },
  {
    q: "What is included in a subscription?",
    a: "Every plan includes continuous review monitoring, policy detection, appeal generation, and alerts. Managed filing is included for eligible brands with verified Brand Registry access. Higher self-serve tiers add larger ASIN limits, faster scans, team access, API access, Slack or Teams alerts, analytics, and priority support. Professional supports multiple brands. Enterprise supports larger multi-brand portfolios and custom operating needs.",
  },
  {
    q: "Will you take down honest reviews about my product?",
    a: "Never. Real customer feedback is off-limits — that's how Amazon's marketplace works. We only flag reviews that break Amazon's own rules: fake reviews, paid reviews, spam, harassment, off-topic shipping complaints, and similar. An honest “this product broke after a week” review stays up.",
  },
  {
    q: "Will this get my Amazon account flagged?",
    a: "Reputely is built to avoid account-risky tactics. Our managed filing workflow uses Brand Registry Support and only targets reviews with a policy basis. We do not use fake accounts, voting manipulation, buyer outreach, incentives, or mass-reporting tactics.",
  },
  {
    q: "How does Reputely submit removal requests to Amazon?",
    a: "Reputely-managed filing is currently available only for brands that give us verified Registered Agent access in Amazon Brand Registry. We prepare and manually file eligible policy reports through Brand Registry Support. Amazon also offers a public one-review Report action and Seller Central's Report Abuse workflow, but we have not verified either as an end-to-end delegated Reputely workflow for brands without Brand Registry.",
  },
  {
    q: "Is Reputely approved by or affiliated with Amazon?",
    a: "No. Reputely is an independent third-party tool, not an Amazon affiliate or approved vendor. We use Amazon's documented Brand Registry role for an authorized third-party representative and keep our process aligned with Amazon's rules.",
  },
  {
    q: "Do you ever contact buyers or reviewers?",
    a: "Never. We do not contact reviewers, ask buyers to edit or remove reviews, offer incentives, or encourage customers to change feedback. That would create exactly the kind of risk Reputely is designed to avoid.",
  },
  {
    q: "Are review removals submitted in bulk?",
    a: "We classify reviews, identify likely policy violations, and prepare a rationale for each one. For eligible brands, an operator may include several reviews in one Brand Registry Support case while preserving the policy basis and URL for every review.",
  },
  {
    q: "What if a review is flagged incorrectly?",
    a: "We mark reviews for action only when there is a clear policy basis. Borderline cases pause for an extra check before filing. You can also flag a review we missed, stop a pending action, or bring up a complex case.",
  },
  {
    q: "Which platforms do you cover?",
    a: "Amazon first — that's where the takedown process is best-developed and where sellers feel the pain most. Walmart, Google reviews, Trustpilot, Yelp, Facebook, and Tripadvisor are next on the list.",
  },
];

export function FAQ({ hideHeader = false }: { hideHeader?: boolean }) {
  return (
    <section id="faq" className="max-w-3xl mx-auto px-6 py-16">
      {!hideHeader && (
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
            Frequently asked questions
          </h2>
        </div>
      )}
      <div className="divide-y divide-slate-200 border-y border-slate-200 dark:divide-slate-800 dark:border-slate-800">
        {faqs.map((f, i) => (
          <FaqItem key={i} question={f.q} answer={f.a} />
        ))}
      </div>
    </section>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left py-5"
      >
        <span className="font-medium text-slate-900 dark:text-slate-100 pr-4">{question}</span>
        <ChevronDown
          className={cn(
            "h-5 w-5 text-slate-500 dark:text-slate-400 transition-transform shrink-0",
            open && "rotate-180"
          )}
        />
      </button>
      {open && (
        <div className="pb-5 text-slate-700 dark:text-slate-300 leading-relaxed">{answer}</div>
      )}
    </div>
  );
}
